import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  Grid
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { PrimaryButton, SecondaryButton } from '@/components/index';
import InfoIcon from '@mui/icons-material/Info';

export interface GenericCSVExportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (separator: string) => void;
  title?: string;
  entityName?: string;
  defaultFilename?: string;
  currentPageInfo?: {
    page: number;
    pageSize: number;
    totalRows: number;
  };
}

interface FormData {
  separator: string;
}

export const GenericCSVExportDialog: React.FC<GenericCSVExportDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title = 'Export to CSV',
  entityName = 'data',
  currentPageInfo,
}) => {
  const { control, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: {
      separator: ';',
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = (data: FormData) => {
    onSubmit(data.separator);
    handleClose();
  };

  const showPaginationWarning = currentPageInfo && currentPageInfo.totalRows > currentPageInfo.pageSize;
  const visibleRows = currentPageInfo ? Math.min(currentPageInfo.pageSize, currentPageInfo.totalRows) : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {showPaginationWarning && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" icon={<InfoIcon/>}>
                  Only the current page will be exported ({visibleRows} of {currentPageInfo?.totalRows} {entityName}).
                  <br />The export will include only the visible columns in their current order.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Controller
                name="separator"
                control={control}
                rules={{ 
                  required: 'CSV separator is required',
                  minLength: { value: 1, message: 'Separator must be at least 1 character' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CSV Separator"
                    fullWidth
                    error={!!errors.separator}
                    helperText={errors.separator?.message || "Character to separate CSV columns (default: ;)"}
                    slotProps={{ htmlInput: { maxLength: 1 } }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSubmit(onFormSubmit)} type="submit">
          Export CSV
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
